import { Component, OnInit } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { AccessibilityCheckNameService } from '@client/app/modules/accessibility/services/accessibility-check-name/accessibility-check-name.service';
import { UnderscorelessPipe } from '../../pipes/underscoreless.pipe';
import { GlobalHelperService } from '@client/app/services/global-helper/global-helper.service';

interface RelationshipChangeValue {
    [key: string]: string | { constraint?: string; regex?: string };
}

@Component({
    selector: 'mon-audit-log-view-changes-dialog',
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        UnderscorelessPipe,
        TableModule,
    ],
    templateUrl: './audit-log-view-changes-dialog.component.html',
    styleUrls: ['./audit-log-view-changes-dialog.component.scss'],
})
export class AuditLogViewChangesDialogComponent implements OnInit {
    data: {
        [key: string]: string | Record<string, string[]>;
    } = {};
    name: string | null = null;
    changes: string | Record<string, string[]> = {};
    processedChanges: {
        key: string;
        before: string | string[] | undefined;
        after: string | string[] | undefined;
    }[] = [];
    created: boolean = false;
    relationshipChanges: Record<string, string[]> = {};
    processedRelationshipChanges: {
        key: string;
        before: Array<{ label: string; value: string | undefined }>;
        after: Array<{ label: string; value: string | undefined }>;
    }[] = [];

    constructor (
        private readonly dialogConfig: DynamicDialogConfig,
        private readonly accessibilityCheckNameService: AccessibilityCheckNameService,
        readonly globalHelperService: GlobalHelperService,
    ) {}

    ngOnInit (): void {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        this.data = this.dialogConfig.data.log_data || {};
        this.changes = this.data.changes || {};
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        this.created = this.dialogConfig.data.created || false;
        if (this.data.check_id && typeof this.data.check_id === 'number') {
            this.getAccessibilityCheckName(this.data.check_id).then((name) => {
                this.name = name;
            });
        }
        const relationship_changes = this.data.relationship_changes;
        if (relationship_changes && typeof relationship_changes === 'object' && !Array.isArray(relationship_changes)) {
            for (const key in relationship_changes) {
                if (
                    relationship_changes.hasOwnProperty(key) &&
                    !this.isSimilar(relationship_changes[key][0], relationship_changes[key][1])
                ) {
                    this.relationshipChanges[key] = relationship_changes[key];
                }
            }
        }
        this.processChanges();
    }

    async getAccessibilityCheckName (checkID: number): Promise<string | null> {
        return await this.accessibilityCheckNameService.getName(checkID);
    }

    processChanges () {
        this.processedChanges = Object.entries(this.changes)
            .filter(([key]) => !this.isBlacklisted(key))
            .map(([key, value]: [string, string | string[]]) => {
                const before = value[0];
                const after = value[1];
                return { key, before, after };
            });

        this.processedRelationshipChanges = Object.entries(this.relationshipChanges)
            .map(([key, value]) => {
                const before = this.processRelationshipChangesObj(key,value[0]);
                const after = this.processRelationshipChangesObj(key,value[1]);
                return { key, before, after };
            });
    }

    processRelationshipChangesObj (key: string, obj: string | RelationshipChangeValue): Array<{ label: string; value: string | undefined }> {
        if (!obj) {
            return [];
        }
        if (typeof obj === 'string') {
            return [{ label: key, value: obj }];
        }
        const pathConstraint = 'adminAuditLog.dialog.viewChanges.pathConstraints';
        const linkExcludes = 'adminAuditLog.dialog.viewChanges.linkExcludes';

        return Object.entries(obj)
            .map(([objKey, objValue]) => {
                if (key === 'path_constraints' && typeof objValue === 'object' && objValue !== null && 'constraint' in objValue) {
                    return { label: pathConstraint, value: objValue.constraint?.toString() };
                }
                if (key === 'link_excludes' && typeof objValue === 'object' && objValue !== null && 'regex' in objValue) {
                    return { label: linkExcludes, value: objValue.regex?.toString() };
                }
                if (typeof objValue === 'object' && objValue !== null) {
                    return { label: objKey, value: JSON.stringify(objValue) };
                }
                if (objValue !== undefined && objValue !== null) {
                    return { label: objKey, value: objValue.toString() };
                }
                return { label: objKey, value: undefined };
            });
    }

    isBlacklisted (key: string): boolean {
        return ['updated_at'].indexOf(key) !== -1;
    }

    isSimilar (obj1: string, obj2: string): boolean {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }
}
